import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

// Q Drives palette — AMG red accent + supporting hues for slice differentiation.
// Pies need enough distinct colors for up to ~10 brands; recycle responsibly.
const PIE_COLORS = [
  "#FF1E1E", // AMG red — always the largest slice
  "#F5A623", // amber
  "#22C55E", // green
  "#6FA8DC", // blue
  "#8E44AD", // purple
  "#FF8C42", // orange
  "#14B8A6", // teal
  "#EAB308", // gold
  "#F472B6", // pink
  "#94A3B8", // slate (fallback)
];

const SCOPE_OPTS = [
  { v: "active", l: "Active" },
  { v: "all",    l: "All" },
];

function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(1)}%`;
}

// Recharts label renderer — only show % on slices >=4% to avoid clutter.
function renderSliceLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (!percent || percent < 0.04) return null;
  const RAD = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.6;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  return (
    <text
      x={x}
      y={y}
      fill="#0A0A0A"
      textAnchor="middle"
      dominantBaseline="central"
      className="font-mono-data"
      style={{ fontSize: 11, fontWeight: 700 }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

function PieTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-[#0E0E0E] border border-[#2A2A2A] px-3 py-2 text-xs font-mono-data">
      <div className="text-white font-display normal-case mb-1">{row.key}</div>
      <div className="text-white/70">Count: <span className="text-white">{row.count}</span></div>
      <div className="text-white/70">Share: <span className="text-white">{fmtPct(row.pct)}</span></div>
      {row.avg_liquidity !== null && row.avg_liquidity !== undefined && (
        <div className="text-white/70">Avg Liquidity: <span className="text-white">{row.avg_liquidity}/10</span></div>
      )}
      {row.avg_gap_pct !== null && row.avg_gap_pct !== undefined && (
        <div className="text-white/70">Avg Gap: <span className="text-white">{fmtPct(row.avg_gap_pct)}</span></div>
      )}
    </div>
  );
}

// Single pie card — handles its own colors, table, and empty state.
function MixPieCard({ title, subtitle, rows, unknownCount, showAvgs = false, testid }) {
  const total = useMemo(() => rows.reduce((a, b) => a + b.count, 0), [rows]);
  const colored = useMemo(
    () => rows.map((r, i) => ({ ...r, _fill: PIE_COLORS[i % PIE_COLORS.length] })),
    [rows]
  );

  return (
    <div className="qd-card p-5" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="qd-overline">{title}</div>
          {subtitle && <div className="text-[11px] text-white/45 mt-1">{subtitle}</div>}
        </div>
        <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/40">
          {total} unit{total === 1 ? "" : "s"}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="py-10 text-center text-xs text-white/40 font-mono-data tracking-wide">
          No data yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4 items-center">
          {/* Pie */}
          <div className="h-[200px] w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <PieChart>
                <Pie
                  data={colored}
                  dataKey="count"
                  nameKey="key"
                  innerRadius={36}
                  outerRadius={86}
                  paddingAngle={1}
                  stroke="#0A0A0A"
                  strokeWidth={2}
                  label={renderSliceLabel}
                  labelLine={false}
                  isAnimationActive={false}
                >
                  {colored.map((r) => (
                    <Cell key={r.key} fill={r._fill} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Legend / breakdown table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono-data" data-testid={`${testid}-table`}>
              <thead>
                <tr className="text-[10px] tracking-widest uppercase text-white/40 border-b border-[#1A1A1A]">
                  <th className="px-2 py-1.5 text-left">{title.replace(" Distribution", "").replace(" Combination", "")}</th>
                  <th className="px-2 py-1.5 text-right">Count</th>
                  <th className="px-2 py-1.5 text-right">%</th>
                  {showAvgs && (
                    <>
                      <th className="px-2 py-1.5 text-right">Avg Liq</th>
                      <th className="px-2 py-1.5 text-right">Avg Gap</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {colored.map((r) => (
                  <tr
                    key={r.key}
                    className="border-b border-[#141414]"
                    data-testid={`${testid}-row-${String(r.key).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-2.5 h-2.5"
                          style={{ background: r._fill }}
                          aria-hidden
                        />
                        <span className="text-white">{r.key}</span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right text-white">{r.count}</td>
                    <td className="px-2 py-1.5 text-right text-white/80">{fmtPct(r.pct)}</td>
                    {showAvgs && (
                      <>
                        <td className="px-2 py-1.5 text-right text-white/80">
                          {r.avg_liquidity === null || r.avg_liquidity === undefined ? "—" : `${r.avg_liquidity}/10`}
                        </td>
                        <td className="px-2 py-1.5 text-right text-white/80">
                          {fmtPct(r.avg_gap_pct)}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {unknownCount > 0 && (
                  <tr className="text-white/40">
                    <td className="px-2 py-1.5 italic">Unmapped / missing</td>
                    <td className="px-2 py-1.5 text-right">{unknownCount}</td>
                    <td className="px-2 py-1.5 text-right">—</td>
                    {showAvgs && <><td className="px-2 py-1.5" /><td className="px-2 py-1.5" /></>}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function InventoryBreakdown() {
  const [scope, setScope] = useState("active");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/analytics/inventory-breakdown?scope=${scope}`)
      .then((r) => !cancelled && setData(r.data))
      .catch(() => toast.error("Failed to load inventory breakdown"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [scope]);

  return (
    <section className="mb-8" data-testid="inventory-breakdown">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="qd-overline">Inventory Breakdown</div>
          <div className="text-[11px] text-white/45 mt-1">
            Primary inventory mix dashboard — Brand · Year · Fuel · Transmission · Powertrain.
            {data?.total_vehicles !== undefined && (
              <span className="text-white/30 ml-2">· {data.total_vehicles} vehicles in scope</span>
            )}
          </div>
        </div>
        <div className="inline-flex border border-[#2A2A2A] bg-[#0E0E0E]" data-testid="inv-scope-tabs">
          {SCOPE_OPTS.map((s) => (
            <button
              key={s.v}
              data-testid={`inv-scope-${s.v}`}
              onClick={() => setScope(s.v)}
              className={`px-3 py-2 text-[10px] font-mono-data tracking-widest uppercase border-r border-[#2A2A2A] last:border-r-0 transition-colors ${
                scope === s.v ? "bg-[#FF1E1E]/15 text-[#FF1E1E]" : "text-white/55 hover:text-white"
              }`}
            >
              {s.l}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="qd-card p-10 text-center text-xs text-white/40 font-mono-data tracking-wide">
          Loading inventory mix…
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {/* Brand — only chart that surfaces Avg Liq + Avg Gap per spec. */}
          <MixPieCard
            title="Brand Distribution"
            subtitle="Make-level concentration"
            rows={data?.brand?.rows || []}
            unknownCount={data?.brand?.unknown_count || 0}
            showAvgs
            testid="inv-brand"
          />
          <MixPieCard
            title="Model Year Distribution"
            subtitle="2025+ · 2024 · 2023 · 2022 · 2021 · 2020 · 2019 · 2018 and older"
            rows={data?.year?.rows || []}
            unknownCount={data?.year?.unknown_count || 0}
            testid="inv-year"
          />
          <MixPieCard
            title="Fuel Distribution"
            subtitle="Petrol · Diesel · CNG · EV"
            rows={data?.fuel?.rows || []}
            unknownCount={data?.fuel?.unknown_count || 0}
            testid="inv-fuel"
          />
          <MixPieCard
            title="Transmission Distribution"
            subtitle="Manual vs Automatic"
            rows={data?.transmission?.rows || []}
            unknownCount={data?.transmission?.unknown_count || 0}
            testid="inv-transmission"
          />
          {/* Powertrain spans full width on xl — most actionable combo. */}
          <div className="xl:col-span-2">
            <MixPieCard
              title="Powertrain Combination"
              subtitle="Petrol Manual · Petrol Automatic · Diesel Manual · Diesel Automatic"
              rows={data?.powertrain?.rows || []}
              unknownCount={data?.powertrain?.unknown_count || 0}
              testid="inv-powertrain"
            />
          </div>
        </div>
      )}
    </section>
  );
}
